// frontend/src/App.js
import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import { ModalProvider } from "./context/modalContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminProtectedRoute from "./routes/AdminProtectedRoute";
import HomeRoute from "./routes/HomeRoute";

import Header from "./pages/Header";
import Footer from "./pages/Footer";
import Login from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import FacebookAuthCallback from "./pages/FacebookAuthCallback";
import ModuleAccessPage from "./pages/ModuleAccessPage";

import "./App.css";

const AboutPage = lazy(() => import("./pages/AboutPage"));
const UserGuidePage = lazy(() => import("./pages/UserGuidePage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));

// ── Admin pages (lazy loaded) ─────────────────────────────────────────────
const AdminLogin = lazy(() => import("./admin/pages/AdminLogin"));
const AdminAuthCallback = lazy(() => import("./admin/pages/AdminAuthCallback"));
const AdminDashboard = lazy(() => import("./admin/pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./admin/pages/AdminUsers"));
const AdminContent = lazy(() => import("./admin/pages/AdminContent"));
const AdminRevenue = lazy(() => import("./admin/pages/AdminRevenue"));
const AdminTeachers = lazy(() => import("./admin/pages/AdminTeachers"));
const AdminAdmins     = lazy(() => import("./admin/pages/AdminAdmins"));
const AdminAnalytics  = lazy(() => import("./admin/pages/AdminAnalytics"));
// ─────────────────────────────────────────────────────────────────────────
// Lazy load feature modules
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ReadingModulePage = lazy(() =>
  import("./modules/reading/ReadingModulePage")
);
const StudentReadingDetails = lazy(() =>
  import("./modules/teachers/StudentReadingDetails")
);
const ComprehensionList = lazy(() =>
  import("./modules/writing/ComprehensionList")
);
const SingleComprehensionPage = lazy(() =>
  import("./modules/writing/SingleComprehensionPage")
);
// Import your OnboardingPage
const OnboardingPage = lazy(() => import("./pages/Onboarding")); // Assuming Onboarding.jsx is in src/pages/

// Add Teacher Dashboard import - NOW IN MODULES FOLDER
const TeacherDashboard = lazy(() =>
  //commenting this line below as this is the previous teacherDashboard where student were visible to teacehr or can be searched by teacher. If want to implement this then uncomment this and comment the present one
  //import("./modules/teachers/TeacherDashboard")
  //this is the new file for teacher's dashbaord used at present
  import("./modules/teachers/TeacherDashboardV2")
);

const TeacherComprehensionDetailPage = lazy(() =>
  import("./modules/teachers/TeacherComprehensionDetailPage")
);

const CreatePassagePage = lazy(() =>
  import("./modules/teachers/CreatePassagePage")
);

const CreateWritingComprehensionPage = lazy(() =>
  import("./modules/teachers/CreateWritingComprehensionPage")
);

// Payment & Billing Routes
const PricingPage = lazy(() => import("./pages/PricingPage"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const BillingCancel = lazy(() => import("./pages/BillingCancel"));
const MyPlanPage = lazy(() => import("./pages/MyPlanPage"));

function AppShell() {
  const location = useLocation();
  // Admin routes render their own layout (dark sidebar + topbar).
  // Suppress the student Header and Footer on all /admin/* paths.
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <ModalProvider>
      <div className="App">
        {!isAdminRoute && <Header />}

        <main className="main-content">
          <Suspense fallback={<div className="loading-fallback">Loading...</div>}>
            <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth/facebook/callback" element={<FacebookAuthCallback />} />
                  <Route path="/choose-access" element={<ModuleAccessPage />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reading"
                    element={
                      <ProtectedRoute>
                        <ReadingModulePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/writing"
                    element={
                      <ProtectedRoute>
                        <ComprehensionList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/comprehensions"
                    element={
                      <ProtectedRoute>
                        <ComprehensionList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/comprehensions/:encodedParams"
                    element={
                      <ProtectedRoute>
                        <SingleComprehensionPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Add Teacher Dashboard Route - FROM MODULES FOLDER */}
                  <Route
                    path="/teacher/dashboard"
                    element={
                      <ProtectedRoute>
                        <TeacherDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teacher/create-passage"
                    element={
                      <ProtectedRoute>
                        <CreatePassagePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teacher/create-writing-comprehension"
                    element={
                      <ProtectedRoute>
                        <CreateWritingComprehensionPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teacher/writing-comprehension/:encodedParams"
                    element={
                      <ProtectedRoute>
                        <TeacherComprehensionDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/teacher/reading-comprehension/:encodedParams"
                    element={
                      <ProtectedRoute>
                        <StudentReadingDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <HomeRoute>
                        <Dashboard />
                      </HomeRoute>
                    }
                  />
                  {/* Stripe Billing Routes */}
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/user-guide" element={<UserGuidePage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  <Route
                    path="/my-plan"
                    element={
                      <ProtectedRoute>
                        <MyPlanPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing/success"
                    element={
                      <ProtectedRoute>
                        <BillingSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing/cancel"
                    element={
                      <ProtectedRoute>
                        <BillingCancel />
                      </ProtectedRoute>
                    }
                  />

                  {/* ── Admin Routes ── completely separate from student/teacher ── */}
                  {/* Public admin pages (no auth required) */}
                  <Route
                    path="/admin/analytics"
                    element={
                      <AdminProtectedRoute>
                        <AdminAnalytics />
                      </AdminProtectedRoute>
                    }
                  />

                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/auth/callback" element={<AdminAuthCallback />} />

                  {/* Protected admin pages — require valid admin_access_token cookie */}
                  <Route
                    path="/admin"
                    element={
                      <AdminProtectedRoute>
                        <AdminDashboard />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <AdminProtectedRoute>
                        <AdminUsers />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/content"
                    element={
                      <AdminProtectedRoute>
                        <AdminContent />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/revenue"
                    element={
                      <AdminProtectedRoute>
                        <AdminRevenue />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/teachers"
                    element={
                      <AdminProtectedRoute>
                        <AdminTeachers />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/admins"
                    element={
                      <AdminProtectedRoute>
                        <AdminAdmins />
                      </AdminProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </main>

            {!isAdminRoute && <Footer />}
          </div>
        </ModalProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AdminProvider>
          <AppShell />
        </AdminProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
